/** Interaction only: send intents, fence obsolete responses, apply server decisions and one snapshot. */
import { useCallback, useRef, useState } from 'react'
import { isCancelledError } from '@tanstack/react-query'
import { studyBuilderEvaluationMaxEdits } from '@testfeed/shopify-contracts'
import type {
  StudyBuilderSnapshot,
  ShopifyStudyBuilderEvaluationResponse as Evaluation,
  ShopifyStudyBuilderEvaluationRequest as Request,
} from '@testfeed/shopify-contracts'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import type { StudyBuilderLifecycle } from './studyBuilderLifecycle'
import type { PendingEdits } from './usePendingEdits'
import { isMerchantFacingError } from '../../merchantErrors'
import { requestStudyBuilderEvaluation } from './studyBuilderEvaluationClient'
import { useBuilderEvaluationWork, useBuilderEvaluationInput } from '../outside/useBuilderEvaluationWork'
import { useBuilderEvaluationQuery } from './useBuilderEvaluationQuery'
import { shopifyUiTracker, type ShopifyUiActionId } from '../../shopifyUiTracking'

export type EvaluationOutcome =
  | { status: 'accepted'; result: Evaluation }
  | { status: 'superseded' | 'cancelled' | 'failed' }

type Form = { state: { values: StudyBuilderSnapshot } }
export function useStudyBuilderEvaluation(input: {
  shopify: ShopifyTokenSource
  form: Form
  visit: string
  apply: (snapshot: StudyBuilderSnapshot) => void
  draftId: string
  studyId?: string
  setupId: string | null
  lifecycle: StudyBuilderLifecycle
  onEdited: () => void
  onMerchantEdit?: () => void
  onAuthoringState: (state: Evaluation['authoringState']) => void
  /** Edits the Worker has not acknowledged yet. Owned by the caller so "unsaved" can count them. */
  pending: PendingEdits
}) {
  const active = useRef(false)
  const { checking, cancel: cancelQuery, run: runQuery } = useBuilderEvaluationQuery(input.visit, input.draftId)
  // One generic re-evaluation timer: when a response says it is still being prepared (`retryAfterMs`, set while an AI
  // wording-suggestion batch generates), we re-send the same request after that delay. Not suggestion-specific; cleared
  // whenever a newer evaluation starts or the builder stops, so it never races a live request.
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearPoll = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
  }
  // Product choices await server evaluation before entering the form. Keep that
  // in-flight intent when a Save/Prepare click asks for a fresh source check.
  const pendingInput = useRef<{ captured: StudyBuilderSnapshot; proposed: StudyBuilderSnapshot } | null>(null)
  const accepted = useRef(input.form.state.values)
  // Audience edits the server has not acknowledged yet. Each request carries all of them, in order, so an aborted or
  // superseded request never loses one; the app maps nothing, the server decides what an edit means.
  const { read: readOutbox, set: setOutbox, list: pendingEdits } = input.pending
  const audienceSourcesRef = useRef<Evaluation['audienceSources']>(null)
  // Which guided tour to offer. Only an answer that checked the account with Web carries it; later answers leave it as is.
  const [guidance, setGuidance] = useState<NonNullable<Evaluation['guidance']> | null>(null)
  // Whether the account check has already answered which tour to offer (a null answer counts). While false, each request
  // asks for it (`wantsGuidance`), which forces that one fresh Web check; once answered, the account pass stands in again.
  const guidanceResolved = useRef(false)
  const availabilityRef = useRef<Evaluation['audienceAvailability']>(null)
  const kindRef = useRef<'product' | null>(null)
  const [projection, setProjection] = useState<Evaluation | null>(null)
  const current = useBuilderEvaluationInput(input)
  const [sourceError, setSourceError] = useState(false)
  // Keep the failed request's ownership even when a 4xx refusal removes its edits from the outbox.
  const [failedEdits, setFailedEdits] = useState<NonNullable<Request['edits']>>([])
  const cancel = useCallback(() => {
    cancelQuery()
    clearPoll()
    pendingInput.current = null
  }, [cancelQuery])
  const evaluate = useCallback(
    async function evaluateFn(proposed: StudyBuilderSnapshot, delay = 0): Promise<EvaluationOutcome> {
      if (!active.current) return { status: 'cancelled' }
      const ctx = current.current
      clearPoll()
      const token = ctx.lifecycle.beginFeedback('evaluate-change')
      const captured = ctx.form.state.values
      pendingInput.current = { captured, proposed }
      const request: Request = {
        // Correlation echoed by the Worker, not a freshness gate. Query's unique attempt key owns cancellation.
        identity: { visit: ctx.visit, sequence: Date.now() },
        draftId: ctx.draftId,
        ...(ctx.studyId ? { studyId: ctx.studyId } : {}),
        setupId: ctx.setupId,
        kind: kindRef.current,
        before: accepted.current,
        proposed,
        confirmFingerprint: null,
        audienceAvailability: availabilityRef.current,
        audienceSources: audienceSourcesRef.current,
        ...(guidanceResolved.current ? {} : { wantsGuidance: true }),
        edits: readOutbox().slice(),
      }
      // The same objects stay in the outbox, so they can be removed by identity once this request settles.
      const sent = request.edits ?? []
      const settle = () => {
        setOutbox(readOutbox().filter((edit) => !sent.includes(edit)))
      }
      try {
        return await runQuery(async (signal): Promise<EvaluationOutcome> => {
          try {
            const result = await requestStudyBuilderEvaluation(ctx.shopify, request, signal)
            signal.throwIfAborted()
            if (
              result.identity.visit !== request.identity.visit ||
              result.identity.sequence !== request.identity.sequence
            )
              throw new Error('STUDY_BUILDER_EVALUATION_IDENTITY_MISMATCH')
            if (ctx.form.state.values !== captured) {
              if (token) ctx.lifecycle.finishFeedback(token, 'cancelled')
              return { status: 'superseded' }
            }
            ctx.onAuthoringState(result.authoringState)
            pendingInput.current = null
            availabilityRef.current = result.audienceAvailability ?? null
            audienceSourcesRef.current = result.audienceSources ?? null
            // A guidance field (even null) means the account check answered which tour to offer: stop asking, keep the answer.
            if (result.guidance !== undefined) {
              guidanceResolved.current = true
              setGuidance(result.guidance)
            }
            if (result.requiresConfirmation) throw new Error('STUDY_BUILDER_CONFIRMATION_RETIRED')
            if (token) ctx.lifecycle.finishFeedback(token, 'accepted')
            setFailedEdits([])
            settle()
            accepted.current = result.snapshot
            kindRef.current = result.kind
            setProjection(result)
            // Keep the acknowledged Save baseline. Evaluation itself is never a Save.
            if (JSON.stringify(ctx.form.state.values) !== JSON.stringify(result.snapshot)) ctx.apply(result.snapshot)
            // Still being prepared: ask again after the delay the Worker set, with the latest form values.
            if (result.retryAfterMs !== null && result.retryAfterMs !== undefined)
              pollTimer.current = setTimeout(() => {
                pollTimer.current = null
                if (active.current) void evaluateFn(current.current.form.state.values)
              }, result.retryAfterMs)
            return { status: 'accepted', result }
          } catch (error) {
            // Cancellation cannot publish a failure, settle edits, or clear a newer request's pending input.
            signal.throwIfAborted()
            pendingInput.current = null
            // A 4xx answer is the Worker refusing what this request carried; sending it again fails the same way, so the
            // edits it carried are dropped. A network or server failure keeps them queued.
            setFailedEdits(sent)
            if (isMerchantFacingError(error) && error.status !== undefined && error.status >= 400 && error.status < 500)
              settle()
            if (token) ctx.lifecycle.failFeedback(token, error)
            throw error
          }
        }, delay)
      } catch (error) {
        if (isCancelledError(error)) {
          if (token) ctx.lifecycle.finishFeedback(token, 'cancelled')
          return { status: 'superseded' }
        }
        return { status: 'failed' }
      }
    },
    [runQuery],
  )
  const start = useCallback(() => {
    active.current = true
    void evaluate(current.current.form.state.values)
  }, [evaluate])
  const stop = useCallback(() => {
    active.current = false
    cancel()
  }, [cancel])
  useBuilderEvaluationWork(start, stop)
  const changed = useCallback(
    (next: StudyBuilderSnapshot, immediate = false, apply = true) => {
      if (!active.current) return Promise.resolve<EvaluationOutcome>({ status: 'cancelled' })
      current.current.onMerchantEdit?.()
      cancel()
      if (apply) current.current.apply(next)
      // Every merchant change is unsaved from the moment it is made, whether the form or the Worker applies it.
      current.current.onEdited()
      return evaluate(next, immediate ? 0 : 350)
    },
    [cancel, evaluate],
  )
  const reportEdit = useCallback(
    (key: string, value: string, pace: 'now' | 'typing' = 'now') => {
      // A newer value for the field just edited replaces the unsent older one, so typing queues one edit, not one per key.
      const last = readOutbox().at(-1)
      // Only typing is replaced: each press of a button is its own edit, however alike they look.
      const replaces = pace === 'typing' && last?.key === key
      const queued = [...(replaces ? readOutbox().slice(0, -1) : readOutbox()), { key, value }]
      // A long run of failed requests must not grow the list past what the Worker accepts: keep the newest edit per field.
      setOutbox(
        queued.length > studyBuilderEvaluationMaxEdits
          ? queued.filter((edit, index) => queued.findLastIndex((other) => other.key === edit.key) === index)
          : queued,
      )
      const work = () => changed(current.current.form.state.values, pace === 'now', false)
      if (pace === 'typing') return work()
      // Only completed choices/buttons, never text values or automatic polling. The edit grammar remains server-owned.
      let action: ShopifyUiActionId = 'select-option'
      if (/^comparisons\.[^.]+\.suggestions\.request$/.test(key)) action = 'open-suggestions'
      else if (/^comparisons\.[^.]+\.suggestions\.more$/.test(key)) action = 'retry-suggestions'
      else if (/^comparisons\.[^.]+\.suggestions\.[^.]+\.[^.]+\.add$/.test(key)) action = 'apply-suggestion'
      else if (key.includes('.suggestions.')) action = 'suggestion-feedback'
      const tracker = shopifyUiTracker(current.current.shopify)
      const attempt = tracker.request(action)
      return work().then((outcome) => {
        if (outcome.status === 'accepted') tracker.completed(attempt)
        else if (outcome.status === 'failed') tracker.failed(attempt, { errorCode: 'REQUEST_FAILED' })
        else tracker.cancelled(attempt, outcome.status === 'superseded' ? 'scope-change' : 'aborted')
        return outcome
      })
    },
    [changed],
  )
  const discard = useCallback(
    (snapshot: StudyBuilderSnapshot) => {
      cancel()
      setOutbox([])
      accepted.current = snapshot
      // Discard keeps the echoed audience sources and the signed shopper count: a failed read is never echoed, and the
      // count is re-read when its audience or 30 s receipt no longer matches, so forcing a fresh read only adds waiting.
      // It still settles the source-check flag, as a reset of the page.
      void evaluate(snapshot).then((outcome) => {
        if (outcome.status === 'accepted') setSourceError(false)
        if (outcome.status === 'failed') setSourceError(true)
      })
    },
    [cancel, evaluate],
  )
  const settleCurrent = useCallback(async (): Promise<EvaluationOutcome> => {
    const snapshot = current.current.form.state.values
    const pending = pendingInput.current
    return evaluate(pending?.captured === snapshot ? pending.proposed : snapshot)
  }, [evaluate])
  return {
    projection,
    guidance,
    settleCurrent,
    pendingEdits,
    failedEdits,
    hasPendingEdits: pendingEdits.length > 0,
    checking,
    sourceError,
    changed,
    edit: reportEdit,
    discard,
    stop,
    retry: start,
  }
}
